// Shared fetch helpers for named, reusable customer eligibility rule sets
// (query-builder layer on top of the flat per-customer eligibility rows).
// Mirrors the request/auth-header/JSON-handling convention established in
// components/AhriDashboard/mappingReview/api.js.

export function makeEligibilityRuleSetsApi(apiBaseUrl, getAuthHeaders) {
  const authHeaders = () => (typeof getAuthHeaders === 'function' ? getAuthHeaders() : {});

  const request = async (path, { method = 'GET', body } = {}) => {
    const resp = await fetch(`${apiBaseUrl}${path}`, {
      method,
      headers: {
        ...authHeaders(),
        ...(body ? { 'Content-Type': 'application/json' } : {}),
      },
      body: body ? JSON.stringify(body) : undefined,
    });
    const data = await resp.json().catch(() => ({}));
    if (!resp.ok) {
      throw new Error(String(data?.detail || resp.statusText || `Request failed (${resp.status})`));
    }
    return data;
  };

  return {
    listRuleSets: (unitType) =>
      request(`/api/v1/ahri/eligibility-rule-sets${unitType ? `?unit_type=${encodeURIComponent(unitType)}` : ''}`),
    createRuleSet: (ruleSet) =>
      request('/api/v1/ahri/eligibility-rule-sets', { method: 'POST', body: ruleSet }),
    updateRuleSet: (id, patch) =>
      request(`/api/v1/ahri/eligibility-rule-sets/${id}`, { method: 'PATCH', body: patch }),
    deleteRuleSet: (id) =>
      request(`/api/v1/ahri/eligibility-rule-sets/${id}`, { method: 'DELETE' }),
    simulateRuleSet: (id) =>
      request(`/api/v1/ahri/eligibility-rule-sets/${id}/simulate`, { method: 'POST' }),
    listCustomerAssignments: (customerId) =>
      request(`/api/v1/customers/${encodeURIComponent(customerId)}/eligibility-rule-assignments`),
    assignRuleSet: (customerId, ruleSetId) =>
      request(`/api/v1/customers/${encodeURIComponent(customerId)}/eligibility-rule-assignments`, {
        method: 'POST',
        body: { rule_set_id: ruleSetId },
      }),
    unassignRuleSet: (customerId, assignmentId) =>
      request(
        `/api/v1/customers/${encodeURIComponent(customerId)}/eligibility-rule-assignments/${assignmentId}`,
        { method: 'DELETE' }
      ),
    getFacetValues: (field, unitType) =>
      request(
        `/api/v1/ahri/eligibility-rule-sets/facet-values?field=${encodeURIComponent(field)}&unit_type=${encodeURIComponent(unitType)}`
      ),
    previewRuleSetsFq: (ruleSetIds) =>
      request('/api/v1/ahri/eligibility-rule-sets/preview-fq', {
        method: 'POST',
        body: { rule_set_ids: ruleSetIds },
      }),
  };
}

// react-querybuilder hardcodes a short list of operator names ("between",
// "notBetween", "in", "notIn") that its internal value-editor hook
// recognizes as "takes an array value"; an unrecognized name (our backend's
// snake_case 'not_in') paired with an array value makes that hook repeatedly
// "correct" the value, which causes an infinite render loop ("Maximum
// update depth exceeded") the moment a rule using it mounts -- reproduced in
// a throwaway RTL test, not a one-off guess. Declared above ELIGIBILITY_FIELDS
// (not just above rqbQueryToPredicate) because textOperators() below is
// invoked at module-eval time, before a `const` declared later would exist.
const RQB_NOT_IN = 'notIn';
const BACKEND_NOT_IN = 'not_in';

// Fields with live, data-driven values fetchable via getFacetValues -- must
// match backend FACETABLE_FIELDS in api/ahri_eligibility_rule_sets.py.
export const FACETABLE_FIELDS = ['manufacturer', 'brand', 'category_taxonomy_path', 'category_name', 'product_code'];

// Field vocabulary for the query-builder UI. `name` values are the exact
// backend operator/field strings (customer_eligibility_predicate.py) so no
// translation table is needed between react-querybuilder and the API.
export const ELIGIBILITY_FIELDS = [
  {
    name: 'manufacturer', label: 'Manufacturer', inputType: 'text',
    operators: textOperators(),
  },
  {
    name: 'brand', label: 'Brand', inputType: 'text',
    operators: textOperators(),
  },
  {
    name: 'category_taxonomy_path', label: 'Category Path', inputType: 'text',
    placeholder: '/hvac/residential',
    operators: textOperators(),
  },
  {
    name: 'category_name', label: 'Category Name', inputType: 'text',
    placeholder: 'Air Conditioners',
    operators: textOperators(),
  },
  {
    name: 'product_code', label: 'Product Code', inputType: 'text',
    operators: textOperators(),
  },
  {
    name: 'is_complete_match', label: 'Complete Match', valueEditorType: 'checkbox', defaultValue: true,
    operators: [{ name: 'eq', label: 'is' }, { name: 'ne', label: 'is not' }],
  },
  {
    name: 'source', label: 'Match Source', inputType: 'text',
    valueEditorType: 'select',
    values: [
      { name: 'auto', label: 'auto' },
      { name: 'manual', label: 'manual' },
      { name: 'ahri_certificate_fallback', label: 'ahri_certificate_fallback' },
    ],
    operators: [
      { name: 'eq', label: 'is' }, { name: 'ne', label: 'is not' },
      { name: 'in', label: 'is one of' }, { name: RQB_NOT_IN, label: 'is not one of' },
      { name: 'exists', label: 'is set' }, { name: 'is_empty', label: 'is not set' },
    ],
  },
  {
    name: 'match_score', label: 'Match Score', inputType: 'number',
    operators: [
      { name: 'eq', label: '=' }, { name: 'ne', label: '!=' },
      { name: 'lt', label: '<' }, { name: 'lte', label: '<=' },
      { name: 'gt', label: '>' }, { name: 'gte', label: '>=' },
      { name: 'between', label: 'between' },
      { name: 'in', label: 'is one of' }, { name: RQB_NOT_IN, label: 'is not one of' },
      { name: 'exists', label: 'is set' }, { name: 'is_empty', label: 'is not set' },
    ],
  },
];

function textOperators() {
  return [
    { name: 'eq', label: 'is' }, { name: 'ne', label: 'is not' },
    { name: 'in', label: 'is one of' }, { name: RQB_NOT_IN, label: 'is not one of' },
    { name: 'contains', label: 'contains' }, { name: 'not_contains', label: 'does not contain' },
    { name: 'starts_with', label: 'starts with' }, { name: 'not_starts_with', label: 'does not start with' },
    { name: 'exists', label: 'is set' }, { name: 'is_empty', label: 'is not set' },
  ];
}

// react-querybuilder RuleGroupType <-> backend AND/OR/NOT predicate tree.
// Almost no operator-name translation is needed -- ELIGIBILITY_FIELDS above
// defines each field's `operators` using the exact backend op strings as
// `name` -- EXCEPT 'not_in', which is translated to/from react-querybuilder's
// own canonical 'notIn' (RQB_NOT_IN/BACKEND_NOT_IN, declared above
// ELIGIBILITY_FIELDS).

export function rqbQueryToPredicate(query) {
  if (!query || !Array.isArray(query.rules)) return { all: [] };
  const key = query.combinator === 'or' ? 'any' : 'all';
  const children = query.rules.map((r) =>
    Object.prototype.hasOwnProperty.call(r, 'rules') ? rqbQueryToPredicate(r) : ruleToCondition(r)
  );
  const node = { [key]: children };
  return query.not ? { not: node } : node;
}

function ruleToCondition(rule) {
  const op = rule.operator === RQB_NOT_IN ? BACKEND_NOT_IN : rule.operator;
  const cond = { field: rule.field, op };
  if (!['exists', 'is_empty'].includes(op)) {
    if (op === 'between') {
      const [lo, hi] = String(rule.value).split(',').map((v) => Number(v.trim()));
      cond.value = [lo, hi];
    } else if (op === 'in' || op === 'not_in') {
      cond.value = Array.isArray(rule.value)
        ? rule.value
        : String(rule.value).split(',').map((v) => v.trim()).filter(Boolean);
    } else {
      cond.value = rule.value;
    }
  }
  return cond;
}

export function predicateToRqbQuery(predicate) {
  if (!predicate || typeof predicate !== 'object') return { combinator: 'and', rules: [] };
  if ('not' in predicate) {
    return { ...predicateToRqbQuery(predicate.not), not: true };
  }
  const combinator = 'any' in predicate ? 'or' : 'and';
  const children = predicate[combinator === 'or' ? 'any' : 'all'] || [];
  return {
    combinator,
    rules: children.map((c) =>
      'field' in c ? conditionToRule(c) : predicateToRqbQuery(c)
    ),
  };
}

function conditionToRule(cond) {
  let value = cond.value;
  // 'in'/'not_in' stay real arrays (react-querybuilder's multiselect editor,
  // used with listsAsArrays, expects an array); 'between' stays a
  // comma-joined string (react-querybuilder's own convention for its
  // two-input range editor).
  if (Array.isArray(value) && cond.op !== 'in' && cond.op !== 'not_in') {
    value = value.join(',');
  }
  const operator = cond.op === BACKEND_NOT_IN ? RQB_NOT_IN : cond.op;
  return { field: cond.field, operator, value: value === undefined ? '' : value };
}
